import { useEffect, useState } from 'react';
import { useUsersData } from '../../../../../Hooks/TT_ELD/Users';
import SearchOptions from '../../../../../Utils/SearchOptions';
import { SearchResultForCompany } from '../../../../../Utils/SearchResults';
import UsersTable from './UsersTable';
import { Button, Grid, Drawer, TableProps } from 'antd';
import SearchInput from '../../../../../Utils/SearchInput';
import useRouteQuery from '../../../../../Utils/useRouterQuery';
import { FilterOutlined } from '@ant-design/icons';
import { useTagQuerySync } from '../../../../../Utils/useTagQuerySync';
import { TableParams } from '../../../../../Utils/TableTypes';

const { useBreakpoint } = Grid;

const Users = () => {
  const screens = useBreakpoint();
  const xs = screens.xs;
  const sm = screens.sm;
  const md = screens.md;
  const xl = screens.xl;
  const xxl = screens.xxl;

  const [companyName, setCompanyName] = useRouteQuery('', 'companyName', String);
  const [companyId, setCompanyId] = useState<string>('');
  const [userName, setUserName] = useRouteQuery('', 'userName', (v) => String(v));
  const [driverName, setDriverName] = useRouteQuery('', 'driverName', (v) => String(v));
  const [driverEmail, setDriverEmail] = useRouteQuery('', 'driverEmail', (v) => String(v));
  const [licenseNumber, setLicenseNumber] = useRouteQuery('', 'licenseNumber', (v) => String(v));
  const [role, setRole] = useRouteQuery('driver', 'role', (v) => String(v));
  const [status, setStatus] = useState<string | null>(null);
  const [uid, setUid] = useRouteQuery('', 'uid', (v) => String(v));
  const [companyUID, setCompanyUID] = useRouteQuery('', 'companyUID', (v) => String(v));
  const [open, setOpen] = useState(false);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const [isMobile, setIsMobile] = useState(false);

  useTagQuerySync(
    { en_US: 'Drivers', zh_CN: '' },
    'users',
    {
      userName,
      driverName,
      driverEmail,
      uid,
      licenseNumber,
      companyUID,
      companyName,
      companyId,
      tableParams,
    },
    {
      userName: setUserName,
      driverName: setDriverName,
      driverEmail: setDriverEmail,
      uid: setUid,
      licenseNumber: setLicenseNumber,
      companyUID: setCompanyUID,
      companyName: setCompanyName,
      companyId: setCompanyId,
      tableParams: setTableParams,
    },
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1960);
    };
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { data, refetch, isLoading, isFetching } = useUsersData(
    companyId,
    role,
    status!,
    driverName,
    driverEmail,
    userName,
    licenseNumber,
    uid,
    companyUID,
    tableParams,
  );
  const handleChange = (event: string, inputName: string) => {
    const updateFunctions: Record<string, (value: string) => void> = {
      name: setDriverName,
      email: setDriverEmail,
      username: setUserName,
      license: setLicenseNumber,
      uid: setUid,
      companyUID: setCompanyUID,
    };

    if (updateFunctions[inputName]) {
      updateFunctions[inputName](event.trim());
    }
  };

  const handleTableChange: TableProps<any>['onChange'] = (pagination, filters, sorter) => {
    if (!Array.isArray(sorter)) {
      const mapOrderToString = (order: 'ascend' | 'descend' | null | undefined): string | null => {
        if (order === 'ascend') return 'true';
        if (order === 'descend') return 'false';
        return null;
      };

      setStatus(mapOrderToString(sorter?.order));
    }

    setTableParams({
      pagination,
      filters,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
  };

  const handleSearchSelect =
    (setter: (val: string) => void, nameSetter?: (val: string) => void) =>
    (val: any, { valId }: { valId: string }) => {
      setter(valId);
      nameSetter?.(val);
      if (valId) {
        setTableParams({
          pagination: {
            current: 1,
          },
        });
      }
    };

  return (
    <div className="mainPage">
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {md && (
          <SearchInput
            value={driverName}
            placeholder="Search by name"
            onDebounceChange={(event: string) => handleChange(event, 'name')}
            enterButtonSize="8"
            searchWidth={'240px'}
          />
        )}

        {md && (
          <SearchInput
            value={driverEmail}
            placeholder="Search by email"
            onDebounceChange={(event: string) => handleChange(event, 'email')}
            enterButtonSize="8"
            searchWidth={'240px'}
          />
        )}
        <SearchInput
          value={userName}
          placeholder={isMobile ? 'Search userName' : ' Search by userName'}
          onDebounceChange={(event: string) => handleChange(event, 'username')}
          enterButtonSize="8"
          searchWidth={'240px'}
        />
        {xxl && (
          <SearchInput
            value={licenseNumber}
            placeholder="Search by license"
            onDebounceChange={(event: string) => handleChange(event, 'license')}
            enterButtonSize="8"
            searchWidth={'240px'}
          />
        )}
        {xxl && (
          <SearchInput
            value={uid}
            placeholder="Search by UID"
            onDebounceChange={(event: string) => handleChange(event, 'uid')}
            enterButtonSize="8"
            searchWidth={'240px'}
          />
        )}
        {xxl && (
          <SearchInput
            value={companyUID}
            placeholder={isMobile ? 'Company UID' : ' Search by Company UID'}
            onDebounceChange={(event: string) => handleChange(event, 'companyUID')}
            enterButtonSize="8"
            searchWidth={'240px'}
          />
        )}
        {xl && (
          <SearchOptions
            SearchResult={SearchResultForCompany}
            value={companyName}
            onSelect={handleSearchSelect(setCompanyId, setCompanyName)}
            placeholder={isMobile ? 'Search company' : ' Search by company'}
            enterButtonSize="8"
            searchWidth={'240px'}
          />
        )}
        <span style={{ display: 'flex', gap: '10px' }}>
          {!xxl && (
            <Button size={'large'} onClick={() => setOpen(true)}>
              <FilterOutlined />
            </Button>
          )}
          {!xs && (
            <Button size={'large'} onClick={() => refetch()}>
              Refresh
            </Button>
          )}
        </span>
      </span>
      <>
        {!xxl && (
          <Drawer
            title={null}
            closable={false}
            placement="bottom"
            onClose={() => setOpen(false)}
            open={open}
            height={xl ? 100 : md ? 130 : sm ? 200 : 340}
          >
            <span className="driver-filter-search">
              {!md && (
                <SearchInput
                  value={driverName}
                  placeholder="Search by name"
                  onDebounceChange={(event: string) => handleChange(event, 'name')}
                  enterButtonSize="8"
                  searchWidth={'100%'}
                />
              )}

              {!md && (
                <SearchInput
                  value={driverEmail}
                  placeholder="Search by email"
                  onDebounceChange={(event: string) => handleChange(event, 'email')}
                  enterButtonSize="8"
                  searchWidth={'100%'}
                />
              )}
              {!xxl && (
                <SearchInput
                  value={licenseNumber}
                  placeholder="Search by license"
                  onDebounceChange={(event: string) => handleChange(event, 'license')}
                  enterButtonSize="8"
                  searchWidth={'100%'}
                />
              )}
              <SearchInput
                value={uid}
                placeholder="Search by UID"
                onDebounceChange={(event: string) => handleChange(event, 'uid')}
                enterButtonSize="8"
                searchWidth={'100%'}
              />
              <SearchInput
                value={companyUID}
                placeholder="Search by CMP UID"
                onDebounceChange={(event: string) => handleChange(event, 'companyUID')}
                enterButtonSize="8"
                searchWidth={'100%'}
              />
              {!xl && (
                <SearchOptions
                  SearchResult={SearchResultForCompany}
                  value={companyName}
                  onSelect={handleSearchSelect(setCompanyId, setCompanyName)}
                  placeholder="Search by company"
                  enterButtonSize="8"
                  searchWidth={'100%'}
                />
              )}
            </span>
          </Drawer>
        )}
      </>
      <UsersTable
        data={data?.data}
        total={data?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={refetch}
        handleTableChange={handleTableChange}
        tableParams={tableParams}
        pagination={false}
      />
    </div>
  );
};

export default Users;
